<template>
  <div v-if="job">
    <b-img-lazy
      fluid-grow
      src="https://picsum.photos/1400/200/?image=388"
      alt="banner"
      class="header-image rounded-0"
      v-bind="mainProps"
    ></b-img-lazy>

    <search-bar />

    <b-modal v-model="show" title="Delete Post?" centered class="p-3">
      <p>
        Are you sure you want to permanently delete this post?
        <br />All details and comments will be deleted.
      </p>
      <hr class="modal-divider" />
      <template v-slot:modal-footer>
        <b-button class="btn btn-secondary" @click="show = false"
          >Cancel</b-button
        >
        <b-button variant="primary" :disabled="loading" @click="onClickDelete">
          <b-spinner small v-if="loading"></b-spinner>Delete
        </b-button>
      </template>
    </b-modal>

    <transition name="bounce">
      <b-container class="p-0 py-5">
        <b-breadcrumb :items="items" class="pl-0 bg-transparent"></b-breadcrumb>
        <div class="d-flex w-100 justify-content-between">
          <div class="page-title">{{ job.title }}</div>
          <div class="mt-4" v-if="job.author_id === user.id">
            <b-button
              variant="light"
              class="btn-action red-text mr-2"
              @click="show = !show"
              >Delete</b-button
            >
            <b-button class="btn-action" variant="light" @click="onClickEdit"
              >Edit</b-button
            >
          </div>
          <div class="mt-4" v-else>
            <b-button variant="light" class="btn-action mr-2">Report</b-button>
            <b-button
              class="btn-favorite px-0"
              variant="light"
              @click="onClickSave"
            >
              <b-img
                :src="
                  job.is_favorite
                    ? require('@/assets/icons/heart-fill-red.svg')
                    : require('@/assets/icons/heart-outline-secondary.svg')
                "
              ></b-img>
            </b-button>
          </div>
        </div>
        <div class="mt-2">{{ job.suburb }}</div>

        <b-carousel
          id="carousel-1"
          v-model="slide"
          :controls="job.photos.length > 1"
          class="mt-3"
        >
          <b-carousel-slide>
            <template v-slot:img>
              <div
                class="preview-container preview-container-2"
                v-if="job.photos.length !== 0"
              >
                <div
                  class="preview-item preview-item-2"
                  v-for="(photo, pIndex) in job.photos"
                  :key="pIndex"
                >
                  <b-img
                    :src="photo.url"
                    alt="Photos"
                    class="d-block rounded-0 mx-auto"
                    height="720"
                  ></b-img>
                </div>
              </div>
            </template>
          </b-carousel-slide>
        </b-carousel>

        <div class="preview-container mt-3" v-if="job.photos.length > 1">
          <div
            class="preview-item"
            :class="pIndex !== job.photos.length - 1 ? 'mr-3' : ''"
            v-for="(preview, pIndex) in job.photos"
            :key="pIndex"
          >
            <b-img-lazy
              :src="preview.url"
              alt="Photos"
              v-bind="mainProps"
              class="rounded-0 h-100"
            ></b-img-lazy>
          </div>
        </div>

        <b-row class="mt-4">
          <b-col md="8" sm="8">
            <b-card class="p-3 mb-4">
              <b-card-title class="font-weight-bold">Description</b-card-title>
              <b-card-text>{{ job.description }}</b-card-text>
            </b-card>

            <comment-list />
          </b-col>

          <b-col md="4" sm="4">
            <b-card>
              <b-card-title class="font-weight-bold">Posted by</b-card-title>
              <b-card-text>
                <div class="d-flex w-100">
                  <div class="mr-3">
                    <b-avatar
                      :src="job.author.avatar ? job.author.avatar.url : ''"
                    ></b-avatar>
                  </div>

                  <div>
                    <div class="font-weight-bold">
                      {{ job.author.full_name }}
                    </div>
                    <div class="small">{{ job.addedDate }}</div>
                  </div>
                </div>
              </b-card-text>

              <hr class="my-4" />

              <div class="d-flex w-100 justify-content-between">
                <b-card-title>Offering</b-card-title>
                <b-card-title class="font-weight-bold"
                  >${{ job.price_offer }}</b-card-title
                >
              </div>
            </b-card>
          </b-col>
        </b-row>
      </b-container>
    </transition>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import SearchBar from '@/components/SearchBar'
import CommentList from '@/components/lists/CommentList'

export default {
  components: {
    SearchBar,
    CommentList
  },

  computed: {
    ...mapState({
      job: state => state.job.jobDetails,
      user: state => state.auth.user
    }),

    items() {
      return [
        { text: this.job.category.label, href: '#' },
        { text: this.job.subcategory.label, href: '#' },
        { text: this.job.suburb, href: '#' },
        { text: this.job.title, active: true }
      ]
    }
  },

  data() {
    return {
      loading: false,
      show: false,
      slide: 0,
      mainProps: {
        center: true,
        blank: true,
        blankColor: '#bbb'
      }
    }
  },

  methods: {
    ...mapActions({
      getJobDetails: 'job/getJobDetails',
      deleteJob: 'job/deleteJob',
      saveJob: 'job/saveJob',
      unsaveJob: 'job/unsaveJob'
    }),

    ...mapMutations({
      clearJobDetails: 'job/clearJobDetails'
    }),

    onClickEdit() {
      this.$router.replace({
        name: 'job.edit-job-page',
        params: {
          id: this.job.id,
          title: this.job.title.replace(/ +/g, '-').toLowerCase()
        }
      })
    },

    async fetchJob(page = 1) {
      if (this.loading) return
      this.loading = true
      await this.getJobDetails(this.$route.params.id)
      this.loading = false
    },

    async onClickDelete() {
      this.loading = true
      await this.deleteJob(this.job.id)
      this.loading = false
      this.show = false
      this.$router.replace({
        name: 'job.my-jobs-page'
      })
    },

    async onClickSave() {
      if (this.job.is_favorite) {
        await this.unsaveJob(this.job.id)
      } else {
        await this.saveJob(this.job.id)
      }
      this.job.is_favorite = !this.job.is_favorite
    }
  },

  created() {
    this.clearJobDetails()
    this.fetchJob()
  }
}
</script>

<style lang="scss" scoped>
.btn-action {
  height: 46px;
  width: 120px;
  background-color: white;
  border: none;
}
.btn-favorite {
  height: 46px;
  width: 50px;
  background-color: white;
  border: none;
}
.header-image {
  height: 200px;
}
.carousel {
  border-radius: 8px;
}
.preview-item {
  width: 265px;
  height: 170px;
}
.red-text {
  color: #fa4856;
}
.preview-container-2 {
  height: 650px;
}
.preview-item-2 {
  width: 100%;
  height: 650px;
}
</style>
